import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../../api/auth.api';
import { useAuthStore } from '../../stores/auth-store';
import { notifications } from '../../utils/notifications';
import type { UpdateProfilePreferencesDto } from '../../types/profile.types';

/**
 * Hook pour récupérer le profil utilisateur
 */
export const useProfile = () => {
  return useQuery({
    queryKey: ['profile'],
    queryFn: authApi.getProfile,
    staleTime: 30000, // 30 secondes
  });
};

/**
 * Hook pour mettre à jour les préférences du profil
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { setProfile } = useAuthStore();

  return useMutation({
    mutationFn: (preferences: UpdateProfilePreferencesDto) => authApi.updateProfile(preferences),
    onSuccess: (updatedProfile) => {
      // Mettre à jour le store local
      setProfile(updatedProfile);
      
      // Invalider le cache pour forcer un refresh
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      
      notifications.success('Préférences mises à jour avec succès !');
    },
    onError: (error: any) => {
      notifications.error(
        `Erreur lors de la mise à jour : ${error.response?.data?.message || error.message}`
      );
    },
  });
};

/**
 * Hook pour changer le mot de passe
 */
export const useChangePassword = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  return useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string; newPassword: string }) =>
      authApi.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      notifications.success('Mot de passe modifié avec succès !');
      if (options?.onSuccess) {
        options.onSuccess();
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors du changement de mot de passe';
      notifications.error(errorMessage);
      if (options?.onError) {
        options.onError(error);
      }
    },
  });
};

/**
 * Hook pour récupérer l'historique de connexion
 */
export const useLoginHistory = () => {
  return useQuery({
    queryKey: ['login-history'],
    queryFn: authApi.getLoginHistory,
    staleTime: 60000, // 1 minute
  });
};

